import type { listenerHandler } from '@rrweb/types';
import { record } from 'rrweb';

import type { IDomRecorder, RecorderConfig } from '@/types';

/**
 * Wraps rrweb behind the IDomRecorder abstraction.
 *
 * Dependency-Inversion benefit: the Recorder class depends on IDomRecorder,
 * not on rrweb. Swapping to a different capture library (or mocking in tests)
 * only requires a new class that satisfies this interface.
 */
export class RrwebDomRecorder implements IDomRecorder {
  private stopFn: listenerHandler | null = null;
  private started = false;

  constructor(
    private readonly config: Pick<
      RecorderConfig,
      | 'mousemoveSampleMs'
      | 'scrollSampleMs'
      | 'recordCanvas'
      | 'collectFonts'
      | 'inlineStylesheet'
      | 'checkoutEveryNms'
      | 'maskInputOptions'
    >
  ) {}

  start(onEvent: (event: unknown) => void): void {
    if (this.started) {
      throw new Error('[RrwebDomRecorder] Already started');
    }

    const stopFn = record({
      emit: onEvent,
      checkoutEveryNms: this.config.checkoutEveryNms ?? 10_000,
      recordCanvas: this.config.recordCanvas ?? false,
      collectFonts: this.config.collectFonts ?? true,
      // Default true (matches rrweb's own default). Without inlining, cold
      // replay only has the `<link href="…">` tag and the dashboard can't
      // fetch the original CSS — different origin, CORS, or the customer
      // site is offline. Live looks fine because CSS rule additions arrive
      // as incremental events, but those are gone on cold replay.
      inlineStylesheet: this.config.inlineStylesheet ?? true,
      maskInputOptions: {
        color: false,
        date: false,
        'datetime-local': false,
        // Mask anything plausibly identifying or sensitive by default. The
        // host page can opt back in per-field via maskInputOptions, but the
        // safe default avoids leaking PII into the replay stream and the
        // agent's live presence preview if a customer forgets to configure.
        email: true,
        month: false,
        number: false,
        range: false,
        search: false,
        tel: true,
        text: false,
        time: false,
        url: false,
        week: false,
        textarea: false,
        select: false,
        password: true,
        ...this.config.maskInputOptions,
      },
      sampling: {
        mousemove: this.config.mousemoveSampleMs ?? 50,
        mouseInteraction: true,
        scroll: this.config.scrollSampleMs ?? 100,
      },
    });

    if (!stopFn) {
      throw new Error(
        '[RrwebDomRecorder] rrweb record() returned null — check browser compatibility'
      );
    }

    this.stopFn = stopFn;
    this.started = true;
  }

  addCustomEvent(tag: string, payload: unknown): void {
    if (!this.started) {
      throw new Error('[RrwebDomRecorder] Cannot add custom event before start()');
    }
    record.addCustomEvent(tag, payload);
  }

  stop(): void {
    this.stopFn?.();
    this.stopFn = null;
    this.started = false;
  }
}
